<template>
  <view
    v-if="rendered"
    class="act004-quiz-overlay"
    :class="overlayAnimClass"
    @touchmove.stop.prevent
  >
    <view class="act004-quiz-dialog" :class="cardAnimClass" @tap.stop>
      <view class="act004-quiz-dialog__header">
        <text class="act004-quiz-dialog__title">{{ dialogTitle }}</text>
        <text class="act004-quiz-dialog__close" @tap="handleClose">×</text>
      </view>
      <view v-if="sessionLoading" class="act004-quiz-dialog__loading">
        <text>题目加载中...</text>
      </view>
      <view v-if="showQuestion" class="act004-quiz-dialog__progress">
        {{ progressText }}
      </view>
      <view v-if="showQuestion" class="act004-quiz-dialog__body" :class="bodyAnimClass">
        <act004-quiz-question
          ref="questionRef"
          :key="questionRenderKey"
          :question="currentQuestion"
          @change="onAnswerChange"
        />
      </view>
      <view class="act004-quiz-dialog__footer">
        <button class="act004-quiz-dialog__btn" :disabled="submitting" @tap="handleSubmit">
          {{ submitBtnText }}
        </button>
      </view>
    </view>
  </view>
</template>

<script>
import { startAct004QuizSession, submitAct004QuizAnswer } from '@/api/signstore'
import {
  showAct004QuizCorrectAnswerModal,
  showAct004QuizWrongAnswerModal
} from '@/utils/activity-multi/act004-quiz-wrong-answer.js'

import Act004QuizQuestion from './act004-quiz-question.vue'

const ANIM_MS = 300

export default {
  name: 'Act004QuizDialog',
  components: { Act004QuizQuestion },
  props: {
    visible: { type: Boolean, default: false },
    activityUserId: { type: [Number, String], default: null },
    activityCode: { type: String, default: '' },
    pageIndex: { type: Number, default: 0 },
    pointIndex: { type: [Number, String], default: null },
    quizPayload: { type: Object, default: null }
  },
  data() {
    return {
      rendered: false,
      animPhase: '',
      closeTimer: null,
      sessionId: '',
      sessionQuestions: [],
      wrongRetryMode: 'RESTART_FROM_FIRST',
      questionOrderMode: 'FIXED',
      currentIndex: 0,
      questionRenderKey: 0,
      questionAnimPhase: '',
      sessionReady: false,
      sessionLoading: false,
      submitting: false,
      answerPayload: null
    }
  },
  computed: {
    overlayAnimClass() {
      if (this.animPhase === 'enter') return 'act004-quiz-overlay--enter'
      if (this.animPhase === 'leave') return 'act004-quiz-overlay--leave'
      return ''
    },
    cardAnimClass() {
      if (this.animPhase === 'enter') return 'act004-quiz-dialog--enter'
      if (this.animPhase === 'leave') return 'act004-quiz-dialog--leave'
      return ''
    },
    bodyAnimClass() {
      if (this.questionAnimPhase === 'enter') return 'act004-quiz-dialog__body--enter'
      return ''
    },
    questions() {
      if (!this.sessionReady) return []
      return Array.isArray(this.sessionQuestions) ? this.sessionQuestions : []
    },
    totalCount() {
      return this.questions.length
    },
    currentQuestion() {
      return this.questions[this.currentIndex] || null
    },
    showQuestion() {
      return this.sessionReady && !!this.currentQuestion
    },
    progressText() {
      return '第 ' + (this.currentIndex + 1) + ' / ' + this.totalCount + ' 题'
    },
    submitBtnText() {
      return this.submitting ? '提交中...' : '提交本题'
    },
    dialogTitle() {
      const t = this.quizPayload && this.quizPayload.paperTitle
      return t ? String(t) : '答题'
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(v) {
        if (v) {
          this.openWithAnim()
        } else if (this.rendered) {
          this.closeWithAnim()
        }
      }
    }
  },
  beforeDestroy() {
    this.clearCloseTimer()
  },
  methods: {
    clearCloseTimer() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },
    openWithAnim() {
      this.clearCloseTimer()
      this.rendered = true
      this.animPhase = ''
      this.$nextTick(() => {
        this.animPhase = 'enter'
        this.bootstrapSession()
      })
    },
    closeWithAnim() {
      this.clearCloseTimer()
      if (!this.rendered) return
      this.animPhase = 'leave'
      this.closeTimer = setTimeout(() => {
        this.rendered = false
        this.animPhase = ''
        this.resetState()
        this.closeTimer = null
      }, ANIM_MS)
    },
    playQuestionEnterAnim() {
      this.questionAnimPhase = ''
      this.$nextTick(() => {
        this.questionAnimPhase = 'enter'
      })
    },
    resetState() {
      this.sessionId = ''
      this.sessionQuestions = []
      this.wrongRetryMode = 'RESTART_FROM_FIRST'
      this.questionOrderMode = 'FIXED'
      this.currentIndex = 0
      this.questionRenderKey = 0
      this.questionAnimPhase = ''
      this.sessionReady = false
      this.sessionLoading = false
      this.submitting = false
      this.answerPayload = null
    },
    normalizeSessionQuestions(raw) {
      if (!Array.isArray(raw)) return []
      return raw.map((row) => {
        if (!row || typeof row !== 'object') return null
        const opts = Array.isArray(row.options) ? row.options : []
        return {
          questionId: row.questionId,
          questionType: row.questionType,
          title: row.title,
          displaySort: row.displaySort,
          options: opts.map((o) => ({
            optionId: o && o.optionId != null ? o.optionId : null,
            label: o && o.label != null ? String(o.label) : ''
          }))
        }
      }).filter(Boolean)
    },
    async bootstrapSession() {
      this.sessionReady = false
      this.sessionLoading = true
      this.sessionId = ''
      this.sessionQuestions = []
      this.currentIndex = 0
      this.answerPayload = null
      this.questionAnimPhase = ''
      if (!this.activityUserId || !this.quizPayload || !this.quizPayload.quizPaperId) {
        this.sessionLoading = false
        uni.showToast({ title: '问答参数异常', icon: 'none' })
        this.requestClose()
        return
      }
      try {
        const res = await startAct004QuizSession({
          activityUserId: this.activityUserId,
          activityCode: this.activityCode,
          pageIndex: this.pageIndex,
          pointIndex: this.pointIndex,
          quizPaperId: this.quizPayload.quizPaperId
        })
        const data = res && res.data ? res.data : res
        this.sessionId = data && data.sessionId ? data.sessionId : ''
        if (!this.sessionId) {
          throw new Error('创建答题会话失败')
        }
        this.wrongRetryMode = String((data && data.wrongRetryMode) || 'RESTART_FROM_FIRST').toUpperCase()
        this.questionOrderMode = String(
          (data && data.questionOrderMode) ||
            (this.quizPayload.paperRules && this.quizPayload.paperRules.questionOrderMode) ||
            'FIXED'
        ).toUpperCase()
        const fromSession = this.normalizeSessionQuestions(data && data.questions)
        if (fromSession.length) {
          this.sessionQuestions = fromSession
        } else {
          this.sessionQuestions = this.normalizeSessionQuestions(this.quizPayload.questions)
        }
        if (!this.sessionQuestions.length) {
          throw new Error('问答卷暂无题目')
        }
        this.sessionReady = true
        this.sessionLoading = false
        this.questionRenderKey += 1
        this.playQuestionEnterAnim()
      } catch (e) {
        this.sessionLoading = false
        this.sessionReady = false
        uni.showToast({ title: (e && e.message) || '无法开始答题', icon: 'none' })
        this.requestClose()
      }
    },
    onAnswerChange(payload) {
      this.answerPayload = payload
    },
    validateAnswer() {
      const q = this.currentQuestion
      if (!q) return false
      const ref = this.$refs.questionRef
      const payload = ref && ref.getAnswerPayload ? ref.getAnswerPayload() : this.answerPayload
      const type = String(q.questionType || 'SINGLE').toUpperCase()
      if (type === 'TEXT') {
        return !!(payload && String(payload.text || '').trim())
      }
      if (type === 'MULTI') {
        return !!(payload && Array.isArray(payload.optionIds) && payload.optionIds.length)
      }
      return !!(payload && payload.optionId != null)
    },
    clearCurrentAnswer() {
      this.answerPayload = null
      this.questionRenderKey += 1
      this.playQuestionEnterAnim()
    },
    isRetryCurrentMode() {
      return String(this.wrongRetryMode || '').toUpperCase() === 'RETRY_CURRENT'
    },
    requestClose() {
      this.$emit('close')
    },
    handleClose() {
      if (this.animPhase === 'leave') return
      this.requestClose()
    },
    async handleSubmit() {
      if (!this.validateAnswer()) {
        uni.showToast({ title: '请先完成本题', icon: 'none' })
        return
      }
      const ref = this.$refs.questionRef
      const payload = ref && ref.getAnswerPayload ? ref.getAnswerPayload() : this.answerPayload
      this.submitting = true
      try {
        const res = await submitAct004QuizAnswer({
          sessionId: this.sessionId,
          questionId: this.currentQuestion.questionId,
          answerPayload: payload
        })
        const data = res && res.data ? res.data : res
        if (data && data.passed) {
          await showAct004QuizCorrectAnswerModal()
          this.$emit('success', {
            pointIndex: this.pointIndex,
            paperId: this.quizPayload && this.quizPayload.quizPaperId,
            sessionId: this.sessionId
          })
          return
        }
        if (data && data.needRetry) {
          await showAct004QuizWrongAnswerModal()
          if (this.isRetryCurrentMode() && data.finished === false) {
            this.clearCurrentAnswer()
            return
          }
          await this.bootstrapSession()
          return
        }
        if (data && data.isCorrect) {
          await showAct004QuizCorrectAnswerModal()
          this.currentIndex += 1
          this.answerPayload = null
          this.questionRenderKey += 1
          this.playQuestionEnterAnim()
          if (this.currentIndex >= this.totalCount) {
            this.$emit('success', {
              pointIndex: this.pointIndex,
              paperId: this.quizPayload && this.quizPayload.quizPaperId,
              sessionId: this.sessionId
            })
          }
          return
        }
        await showAct004QuizWrongAnswerModal()
      } catch (e) {
        uni.showToast({ title: (e && e.message) || '提交失败', icon: 'none' })
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.act004-quiz-overlay {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40rpx;
  box-sizing: border-box;
  opacity: 0;
}

.act004-quiz-overlay--enter {
  animation: act004-quiz-overlay-in 0.28s ease-out forwards;
}

.act004-quiz-overlay--leave {
  animation: act004-quiz-overlay-out 0.26s ease-in forwards;
}

.act004-quiz-dialog {
  width: 100%;
  max-width: 640rpx;
  background: #fff;
  border-radius: 24rpx;
  padding: 32rpx;
  box-sizing: border-box;
  opacity: 0;
  transform: scale(0.92) translateY(48rpx);
}

.act004-quiz-dialog--enter {
  animation: act004-quiz-card-in 0.32s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.act004-quiz-dialog--leave {
  animation: act004-quiz-card-out 0.26s ease-in forwards;
}

.act004-quiz-dialog__body {
  min-height: 80rpx;
}

.act004-quiz-dialog__body--enter {
  animation: act004-quiz-body-in 0.28s ease-out forwards;
}

.act004-quiz-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16rpx;
}

.act004-quiz-dialog__title {
  font-size: 32rpx;
  font-weight: 600;
  color: #333;
}

.act004-quiz-dialog__close {
  font-size: 44rpx;
  color: #999;
  padding: 0 8rpx;
}

.act004-quiz-dialog__loading {
  padding: 48rpx 0;
  text-align: center;
  font-size: 28rpx;
  color: #999;
}

.act004-quiz-dialog__progress {
  font-size: 24rpx;
  color: #888;
  margin-bottom: 20rpx;
}

.act004-quiz-dialog__footer {
  margin-top: 32rpx;
}

.act004-quiz-dialog__btn {
  background: #409eff;
  color: #fff;
  border-radius: 12rpx;
  font-size: 30rpx;
}

@keyframes act004-quiz-overlay-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes act004-quiz-overlay-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes act004-quiz-card-in {
  from {
    opacity: 0;
    transform: scale(0.92) translateY(48rpx);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes act004-quiz-card-out {
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.94) translateY(32rpx);
  }
}

@keyframes act004-quiz-body-in {
  from {
    opacity: 0;
    transform: translateX(24rpx);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
